import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChatView } from './ChatView';
import { ChatSettingsDialog } from './ChatSettingsDialog';
import { useAuth } from '@/hooks/use-auth';
import {
  useCompanyChatAccess,
  useCompanyChatMessages,
  useCompanyChatMembers,
  useSendCompanyChatMessage,
} from '@/hooks/use-chats';
import { useSocket } from '@/hooks/use-socket';
import type { CompanyChat, CompanyChatMessage } from '@/types';

interface CompanyChatViewProps {
  chat: CompanyChat;
}

export function CompanyChatView({ chat }: CompanyChatViewProps) {
  const [showSettings, setShowSettings] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();

  const { data: access, isLoading: accessLoading } = useCompanyChatAccess(chat.id);
  const { data: messages = [], isLoading: messagesLoading } = useCompanyChatMessages(
    chat.id,
    !!access?.canRead
  );
  const { data: members = [], isLoading: membersLoading } = useCompanyChatMembers(
    chat.id,
    !!access?.canRead
  );
  const sendMessage = useSendCompanyChatMessage(chat.id);

  // Subscribe to chat socket events
  useEffect(() => {
    if (!socket || !isConnected || !access?.canRead) return;

    socket.emit('subscribe:company-chat', { chatId: chat.id });

    const handleNewMessage = (message: CompanyChatMessage) => {
      let appendedNew = false;
      queryClient.setQueryData<CompanyChatMessage[]>(
        ['company-chat-messages', chat.id],
        (old) => {
          // Avoid duplicates
          if (old?.some((m) => m.id === message.id)) return old;
          appendedNew = true;
          return [...(old || []), message];
        }
      );
      if (appendedNew) {
        void queryClient.invalidateQueries({ queryKey: ['all-chats'] });
      }
    };

    socket.on('company-chat:message', handleNewMessage);

    return () => {
      socket.emit('unsubscribe:company-chat', { chatId: chat.id });
      socket.off('company-chat:message', handleNewMessage);
    };
  }, [socket, isConnected, chat.id, access?.canRead, queryClient]);

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!access?.canRead) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">You don't have access to this chat</p>
      </div>
    );
  }

  return (
    <>
      <ChatView
        messages={messages}
        access={access}
        isLoading={messagesLoading}
        isSending={sendMessage.isPending}
        onSendMessage={(content) => sendMessage.mutate(content)}
        onOpenSettings={() => setShowSettings(true)}
        title={chat.name}
        emptyMessage={`No messages in ${chat.name} yet. Start the conversation!`}
        currentUserId={user?.id || ''}
        members={members}
        membersLoading={membersLoading}
      />

      <ChatSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        chatId={chat.id}
        chatType="company"
      />
    </>
  );
}
